import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { Button } from '@la/ds-ui-components';
import { Product, UserSelection } from '@la/types';
import { useRegistration } from 'lib/context/RegistrationContext/RegistrationContext';
import { useRegistrationAddOns } from 'redux/hooks/useRegistrationAddOns';
import { extractFeeRange } from '../../Tournaments/TournamentDetailsPage/utils/feeCalculationUtils';
import { CardDisplayType } from '../MemberRegistration.types';
import styles from './AddOnCard.module.scss';
import AddOnDescription from './AddOnDescription';
import AddOnPriceRange from './AddOnPriceRange';
import AddOnProductImage from './AddOnProductImage';
import AddOnSelections from './AddOnSelections';

export function OptionalAddOnCard({
  addOnData,
  cardDisplayType,
}: Readonly<{ addOnData: Product; cardDisplayType: CardDisplayType }>) {
  const [userSelections, setUserSelections] = useState<UserSelection[]>([]);
  const [addOnQuantity, setAddOnQuantity] = useState(1);
  const [selectionsWithErrors, setSelectionsWithErrors] = useState<string[]>(
    []
  );
  const { description, id, name, imageRef } = addOnData;
  const { maxFee, minFee } = extractFeeRange(addOnData);
  const { currentRegistration } = useRegistration();
  const [searchParams] = useSearchParams();

  const { updateOptionalAddOns } = useRegistrationAddOns();

  function updateAddOnSelection(
    addOnId: number,
    propertyId: string,
    value: string
  ) {
    setUserSelections((prevSelections) => {
      const existingSelectionIndex = prevSelections.findIndex(
        (selection) => selection.optionId === propertyId
      );
      const isEmpty = value.trim() === '';
      const hasExistingSelection = existingSelectionIndex !== -1;

      // If value is empty and no matching selection exists, no change is needed
      // (It should not be possible to submit an empty value but...)
      if (isEmpty && !hasExistingSelection) {
        return prevSelections;
      }

      // If value is empty but has a matching selection, remove the selection
      if (isEmpty && hasExistingSelection) {
        return prevSelections.filter(
          (selection) => selection.optionId !== propertyId
        );
      }

      // If value is not empty and no matching selection exists, add a new selection
      if (!isEmpty && !hasExistingSelection) {
        return [
          ...prevSelections,
          { optionId: propertyId, selectedValue: value },
        ];
      }

      // If value is not empty and a matching selection exists, update the selection
      return prevSelections.map((selection, index) =>
        index === existingSelectionIndex
          ? { ...selection, selectedValue: value }
          : selection
      );
    });
  }

  function handleAddToCart() {
    // validate selections and get the updated errors synchronously
    let newErrors: string[] = [];

    // Check if properties exist before iterating
    if (addOnData.properties) {
      for (const property of addOnData.properties) {
        if (property.required) {
          const propertyIdString = property.id.toString();
          const matchingSelection = userSelections.find(
            (selection: UserSelection) =>
              selection.optionId === propertyIdString
          );

          if (!matchingSelection) {
            // Add error if matchingSelection doesn't exist
            newErrors.push(propertyIdString);
          }
        }
      }
    }

    // Update the state with the new errors
    setSelectionsWithErrors(newErrors);

    if (newErrors.length === 0) {
      // generate productInstanceId from selections
      const prodInstanceId = generateProductInstanceId(
        addOnData.id,
        currentRegistration?.Id || searchParams.get('registrationId') || '',
        userSelections
      );
      // update information in cart
      updateOptionalAddOns({
        addOnType: 'optional',
        addOnId: id,
        productInstanceId: prodInstanceId,
        quantity: addOnQuantity,
        selections: userSelections,
      });
      // reset selections
      setAddOnQuantity(1);
      setUserSelections([]);
    }
  }

  const cardStyle = clsx(styles.addOnCard, styles[cardDisplayType]);

  return (
    <div className={cardStyle}>
      <AddOnProductImage imageRef={imageRef} name={name} />
      <span className={styles.labelAndPricing}>
        <span className={styles.label}>{name}</span>
        <AddOnPriceRange
          maxFee={maxFee}
          minFee={minFee}
          salesTaxRequired={addOnData.salesTaxRequired}
        />
      </span>
      <AddOnDescription description={description} />
      <AddOnSelections
        addOnQuantity={addOnQuantity}
        cardDisplayType={cardDisplayType}
        productId={id}
        properties={addOnData.properties}
        selectionsWithErrors={selectionsWithErrors}
        updateAddOnQuantity={setAddOnQuantity}
        updateAddOnSelection={updateAddOnSelection}
        userSelections={userSelections}
        clearSelectionError={(propertyId: string) => {
          setSelectionsWithErrors((prev) =>
            prev.filter((errId) => errId !== propertyId)
          );
        }}
      />
      <AddToCartButton handleClick={handleAddToCart} />
    </div>
  );
}

export function AddToCartButton({
  handleClick,
}: Readonly<{ handleClick: () => void }>) {
  return (
    <div className={styles.addToCartButton}>
      <Button
        variant="primary"
        size="medium"
        type="submit"
        onClick={handleClick}
      >
        Add to cart
      </Button>
    </div>
  );
}

export function generateProductInstanceId(
  productId: string | number,
  registrationId: string | number,
  selections: UserSelection[]
): string {
  const sortedSelections = sortSelectionsByOptionId(selections);
  const selectionsString = serializeSelections(sortedSelections);
  const combinedString = `${productId}${registrationId}${selectionsString}`;

  return window.btoa(combinedString);
}

function sortSelectionsByOptionId(
  selections: UserSelection[]
): UserSelection[] {
  return [...selections].sort((a, b) =>
    a.optionId.toString().localeCompare(b.optionId.toString())
  );
}

function serializeSelections(selections: UserSelection[]): string {
  return selections
    .map(({ optionId, selectedValue }) => `${optionId}${selectedValue}`)
    .join('');
}
